import { defineStore } from "pinia";
import { computed, ref } from "vue";
import axiosIns from "@/composables/axios.ts";

export interface Plan {
  id: number;
  code: string;
  name: string;
  baseUserLimit: number;
  modules: string[];
  officialWhatsappEnabled: boolean;
  officialWhatsappMessageLimit: number | null;
  monthlyPrice?: PlanPrice;
  yearlyPrice?: PlanPrice;
  createdAt: string;
  updatedAt: string;
}

export interface PlanPrice {
  id: number;
  planId: number;
  planName?: string | null;
  cycle: string;
  price: number;
  extraUserPrice: number;
  isActive: boolean;
  validFrom: string;
  validTo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyPlan {
  id: number;
  companyId: number;
  name: string;
  email: string;
  planId: number;
  plan?: Plan;
  planStartDate?: string;
  planEndDate?: string;
  additionalModules: string[];
  company?: {
    id: number;
    name: string;
    email: string;
  };
  subscription?: any;
}

export enum PaymentMethodType {
  BOLETO = "boleto",
  CREDIT_CARD = "credit_card",
  PIX_RECURRING = "pix_recurring",
}

export interface CreditCardDto {
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  ccv: string;
}

export interface CreditCardHolderInfoDto {
  name: string;
  email: string;
  cpfCnpj: string;
  postalCode: string;
  addressNumber: string;
  addressComplement?: string;
  phone?: string;
  mobilePhone?: string;
}

export interface CreateSubscriptionPayload {
  planPriceId: number;
  paymentMethodType: PaymentMethodType;
  extraUserCount?: number;
  creditCard?: CreditCardDto;
  creditCardHolderInfo?: CreditCardHolderInfoDto;
}

export const usePlansStore = defineStore("plans", () => {
  // State
  const plans = ref<Plan[]>([]);
  const planPrices = ref<PlanPrice[]>([]);
  const companyPlans = ref<CompanyPlan[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const activePlans = computed(() => plans.value.filter((plan) => plan.id));

  // Ordenar planos pela hierarquia: Bronze (1) -> Silver (2) -> Gold (3)
  const sortedPlans = computed(() => {
    const planHierarchy: Record<string, number> = {
      bronze: 1,
      silver: 2,
      gold: 3,
    };

    return [...plans.value].sort((a, b) => {
      const aLevel = planHierarchy[a.code?.toLowerCase() || ""] || 999;
      const bLevel = planHierarchy[b.code?.toLowerCase() || ""] || 999;
      return aLevel - bLevel;
    });
  });

  const plansWithPrices = computed(() =>
    sortedPlans.value.map((plan) => ({
      ...plan,
      features: plan.modules, // Mapear modules para features para compatibilidade
      prices: [plan.monthlyPrice, plan.yearlyPrice].filter(Boolean),
    }))
  );

  const firstThreePlans = computed(() => sortedPlans.value.slice(0, 3));

  // Actions
  const fetchPlans = async () => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.get("/billing/plans");

      plans.value = response.data.map((plan: any) => ({
        ...plan,
        officialWhatsappEnabled: plan.officialWhatsappEnabled ?? true,
        officialWhatsappMessageLimit:
          plan.officialWhatsappMessageLimit ?? null,
      }));
    } catch (err: any) {
      error.value = err.response?.data?.message || "Erro ao carregar planos";
      console.error("Erro ao buscar planos:", err);
    } finally {
      loading.value = false;
    }
  };

  const fetchPlanPrices = async () => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.get("/billing/plans/prices");

      planPrices.value = response.data;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erro ao carregar preços dos planos";
      console.error("Erro ao buscar preços dos planos:", err);
    } finally {
      loading.value = false;
    }
  };

  const fetchCompanyPlans = async () => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.get("/company/plan-configs");

      // Mapear os dados para a estrutura esperada
      companyPlans.value = response.data.map((item: any) => ({
        id: item.id,
        companyId: item.companyId,
        name: item.company?.name || item.name,
        email: item.company?.email || item.email,
        planId: item.planId,
        plan: item.plan,
        planStartDate: item.planStartDate,
        planEndDate: item.planEndDate,
        additionalModules: item.additionalModules || [],
        company: item.company,
        subscription: item.subscription,
      }));
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erro ao carregar planos das empresas";
      console.error("Erro ao buscar planos das empresas:", err);
    } finally {
      loading.value = false;
    }
  };

  const createPlan = async (planData: Partial<Plan>) => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.post("/billing/plans", planData);

      plans.value.push(response.data);

      // Recarregar a lista de planos para garantir consistência
      await fetchPlans();

      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Erro ao criar plano";
      console.error("Erro ao criar plano:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updatePlan = async (id: number, planData: Partial<Plan>) => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.patch(`/billing/plans/${id}`, planData);

      // Recarregar a lista de planos para garantir consistência
      await fetchPlans();

      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Erro ao atualizar plano";
      console.error("Erro ao atualizar plano:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deletePlan = async (id: number) => {
    try {
      loading.value = true;

      await axiosIns.delete(`/billing/plans/${id}`);

      // Recarregar a lista de planos para garantir consistência
      await fetchPlans();
    } catch (err: any) {
      error.value = err.response?.data?.message || "Erro ao excluir plano";
      console.error("Erro ao excluir plano:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createPlanPrice = async (priceData: Partial<PlanPrice>) => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.post("/billing/plans/prices", priceData);

      planPrices.value.push(response.data);

      // Recarregar a lista de preços para garantir consistência
      await fetchPlanPrices();

      return response.data;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erro ao criar preço do plano";
      console.error("Erro ao criar preço do plano:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updatePlanPrice = async (id: number, priceData: Partial<PlanPrice>) => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.patch(
        `/billing/plans/prices/${id}`,
        priceData
      );

      // Recarregar a lista de preços para garantir consistência
      await fetchPlanPrices();

      return response.data;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erro ao atualizar preço do plano";
      console.error("Erro ao atualizar preço do plano:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deletePlanPrice = async (id: number) => {
    try {
      loading.value = true;
      error.value = null;

      await axiosIns.delete(`/billing/plans/prices/${id}`);

      // Recarregar a lista de preços para garantir consistência
      await fetchPlanPrices();
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erro ao excluir preço do plano";
      console.error("Erro ao excluir preço do plano:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateCompanyPlan = async (
    companyId: number,
    planData: {
      planId: number;
      planStartDate?: string;
      planEndDate?: string;
      additionalModules?: string[];
    }
  ) => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.patch(
        `/company/${companyId}/plan-config`,
        planData
      );

      // Recarregar a lista de company plans para refletir as mudanças
      await fetchCompanyPlans();

      return response.data;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erro ao atualizar plano da empresa";
      console.error("Erro ao atualizar plano da empresa:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchCurrentSubscription = async (companyId: number) => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.get(`/billing/subscription/${companyId}`);
      return response.data;
    } catch (err: any) {
      // Se a assinatura não existe (404), retornar null em vez de lançar erro
      // Isso é um caso normal - empresa pode não ter assinatura ainda
      if (err.response?.status === 404) {
        // Não logar erro para 404 - é um caso esperado
        return null;
      }

      error.value =
        err.response?.data?.message || "Erro ao carregar assinatura atual";
      console.error("Erro ao buscar assinatura atual:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createSubscription = async (
    companyId: number,
    payload: CreateSubscriptionPayload
  ) => {
    try {
      loading.value = true;
      error.value = null;

      const response = await axiosIns.post(
        `/billing/subscription/${companyId}/convert-trial`,
        payload
      );

      // Recarregar a assinatura atual após criar
      await fetchCurrentSubscription(companyId);

      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Erro ao criar assinatura";
      console.error("Erro ao criar assinatura:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  return {
    // State
    plans,
    planPrices,
    companyPlans,
    loading,
    error,

    // Getters
    activePlans,
    plansWithPrices,
    firstThreePlans,

    // Actions
    fetchPlans,
    fetchPlanPrices,
    fetchCompanyPlans,
    fetchCurrentSubscription,
    createSubscription,
    createPlan,
    updatePlan,
    deletePlan,
    createPlanPrice,
    updatePlanPrice,
    deletePlanPrice,
    updateCompanyPlan,
    clearError,
  };
});
